import * as React from 'react';
import { Dispatch, SetStateAction, useEffect, useRef } from 'react';
import { classes, useSlug } from '@bloomberg/media-ui-core';
import { GlobalProps } from '@bloomberg/media-ui-core/types';
import { Search as SearchIcon } from '@bloomberg/media-ui-icons';
import MediaServicesSearchClient from '@foundation/media-services-search-client';

import { Logo } from '../Logo';
import { DEFAULT_USER } from '../Nav';
import { useNavContext } from '../Nav/NavContext';
import { SearchDesktop } from '../SearchDesktop';
import { LANGUAGES, NavTips, REGIONS, THEMES, User } from '../types';
import { UserOptions } from '../UserOptions';
import {
  TranslatedMessage,
  useTranslateMessage,
} from '../utils/translate-messages';

import styles from './LogoBar.module.css';

export type SignInButtons = {
  desktopSignInButton?: Element;
  mobileSignInButton?: Element;
};

export type LogoBarProps = GlobalProps & {
  region?: REGIONS;
  language?: LANGUAGES;
  theme?: THEMES;
  isUserOptionsDropdownOpen?: boolean;
  setIsUserOptionsDropdown?: (isOpen: boolean) => void;
  isWeekend?: boolean;
  name?: string;
  user?: User;
  desktopBreakpointClassName?: string;
  searchClient: MediaServicesSearchClient;
  onSignOut: () => void;
  trackingFunction: (action: string, eventLabel: string) => void;
  countryCode?: string;
  subscriptionUrl: string;
  upgradeUrl: string;
  tips?: NavTips;
  handleShowSignInModal?: () => void;
  setSignInButtonRef?: Dispatch<SetStateAction<SignInButtons | undefined>>;
};

export type UserProps = {
  user?: User;
  theme?: THEMES;
  countryCode?: string;
  isDropdownOpen?: boolean;
  setIsDropdownOpen?: (isOpen: boolean) => void;
  onSignOut: () => void;
  trackingFunction: (action: string, eventLabel: string) => void;
  subscriptionUrl: string;
  upgradeUrl: string;
  tips?: NavTips;
  handleShowSignInModal?: () => void;
  setSignInButtonRef?: Dispatch<SetStateAction<SignInButtons | undefined>>;
};

export type SignInButtonProps = {
  text?: string;
  trackingFunction: (action: string, eventLabel: string) => void;
  handleShowSignInModal?: () => void;
  setSignInButtonRef?: Dispatch<SetStateAction<SignInButtons | undefined>>;
};

export const SubscribeButton = ({
  trackingFunction,
  subscriptionUrl,
}: {
  trackingFunction: (action: string, eventLabel: string) => void;
  subscriptionUrl: string;
}) => {
  return (
    <a
      className={styles.subscribeButton}
      href={`${subscriptionUrl}?in_source=nav`}
      target="_blank"
      rel="noreferrer"
      onClick={() => trackingFunction('click', 'subscribe')}
    >
      <TranslatedMessage
        id="media-ui-nav_subscribe"
        defaultMessage="Subscribe"
      />
    </a>
  );
};

export const UpgradeButton = ({
  trackingFunction,
  upgradeUrl,
}: {
  trackingFunction: (action: string, eventLabel: string) => void;
  upgradeUrl: string;
}) => {
  return (
    <a
      className={styles.subscribeButton}
      href={upgradeUrl}
      target="_blank"
      rel="noreferrer"
      onClick={() => trackingFunction('click', 'upgrade')}
    >
      <TranslatedMessage id="media-ui-nav_upgrade" defaultMessage="Upgrade" />
    </a>
  );
};

export const SignInButton = ({
  text,
  trackingFunction,
  handleShowSignInModal,
  setSignInButtonRef,
}: SignInButtonProps) => {
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current) {
      setSignInButtonRef?.((prevState) => {
        return {
          ...prevState,
          desktopSignInButton: ref.current || undefined,
        };
      });
    }
  }, []);

  return (
    <>
      <button
        ref={ref}
        data-testid="sign-in-button"
        className={styles.signInButton}
        onClick={() => {
          trackingFunction('click', 'signIn');
          handleShowSignInModal?.();
        }}
      >
        {text}
      </button>
    </>
  );
};

const UserButton = (props: UserProps) => {
  const {
    isDropdownOpen,
    setIsDropdownOpen,
    user = DEFAULT_USER,
    theme,
    onSignOut,
    trackingFunction,
    subscriptionUrl,
    upgradeUrl,
    tips,
    handleShowSignInModal,
    setSignInButtonRef,
  } = props;
  const translateMessage = useTranslateMessage();

  if (!user.isLoggedIn && !user.isSubscriber)
    return (
      <>
        <SignInButton
          trackingFunction={trackingFunction}
          text={translateMessage({
            id: 'media-ui-nav_signin',
            defaultMessage: 'Sign In',
          })}
          handleShowSignInModal={handleShowSignInModal}
          setSignInButtonRef={setSignInButtonRef}
        />
        <SubscribeButton
          trackingFunction={trackingFunction}
          subscriptionUrl={subscriptionUrl}
        />
      </>
    );

  if (user.isLoggedIn && user.showUpgrade)
    return (
      <>
        <UpgradeButton
          trackingFunction={trackingFunction}
          upgradeUrl={upgradeUrl}
        />
        <UserOptions
          isDropdownOpen={isDropdownOpen}
          setIsDropdownOpen={setIsDropdownOpen}
          user={user}
          theme={theme}
          onSignOut={onSignOut}
        />
      </>
    );

  if (user.isLoggedIn && !user.isSubscriber)
    return (
      <>
        <SubscribeButton
          trackingFunction={trackingFunction}
          subscriptionUrl={subscriptionUrl}
        />
        <UserOptions
          isDropdownOpen={isDropdownOpen}
          setIsDropdownOpen={setIsDropdownOpen}
          user={user}
          theme={theme}
          onSignOut={onSignOut}
        />
      </>
    );

  return (
    <UserOptions
      isDropdownOpen={isDropdownOpen}
      setIsDropdownOpen={setIsDropdownOpen}
      user={user}
      theme={theme}
      onSignOut={onSignOut}
      tips={tips}
    />
  );
};

export const LogoBar = React.forwardRef<HTMLDivElement, LogoBarProps>(
  function LogoBarComponent(props: LogoBarProps, ref) {
    const {
      isUserOptionsDropdownOpen,
      setIsUserOptionsDropdown,
      isWeekend,
      user = DEFAULT_USER,
      name = 'logo-bar',
      theme = THEMES.LIGHT,
      searchClient,
      desktopBreakpointClassName,
      onSignOut,
      trackingFunction,
      countryCode,
      subscriptionUrl,
      upgradeUrl,
      handleShowSignInModal,
      setSignInButtonRef,
      tips,
    } = props;

    const { locale } = useNavContext();
    const showLoginAndSearch = locale === 'en';

    const searchButtonRef = useRef<HTMLButtonElement>(null);

    const [isSearchOpen, setIsSearchOpen] = React.useState(false);
    const componentName = useSlug(name);

    const toggleSearch = () => {
      setIsSearchOpen(!isSearchOpen);
    };

    const onBlur = (e: React.MouseEvent): boolean => {
      if (searchButtonRef.current?.contains(e.target as Node)) return true;

      toggleSearch();

      return true;
    };

    const cx = classes(
      [
        styles.logoBarContainer,
        {
          [styles.light]: theme === THEMES.LIGHT,
          [styles.dark]: theme === THEMES.DARK,
          [styles.blue]: theme === THEMES.BLUE,
        },
      ],
      {
        name,
      }
    );

    return (
      <div className={cx} ref={ref} data-component={componentName}>
        <div className={classes([styles.bar, desktopBreakpointClassName])}>
          <Logo
            trackingFunction={(eventLabel: string) =>
              trackingFunction('click', eventLabel)
            }
            isWeekend={isWeekend}
            theme={theme}
          />
          {showLoginAndSearch && (
            <div className={styles.right}>
              <UserButton
                isDropdownOpen={isUserOptionsDropdownOpen}
                setIsDropdownOpen={setIsUserOptionsDropdown}
                trackingFunction={trackingFunction}
                user={user}
                theme={theme}
                countryCode={countryCode}
                onSignOut={onSignOut}
                subscriptionUrl={subscriptionUrl}
                upgradeUrl={upgradeUrl}
                tips={tips}
                handleShowSignInModal={handleShowSignInModal}
                setSignInButtonRef={setSignInButtonRef}
              />
              <button
                ref={searchButtonRef}
                className={styles.searchButton}
                onClick={toggleSearch}
                data-testid="nav-search-button"
              >
                <SearchIcon />
              </button>
              <div className={styles.search}>
                {isSearchOpen && (
                  <SearchDesktop
                    trackingFunction={trackingFunction}
                    searchClient={searchClient}
                    countryCode={countryCode}
                    desktopBreakpointClassName={desktopBreakpointClassName}
                    onClose={(e: React.MouseEvent) => onBlur(e)}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
);
